import React from "react";
import {Box, Stack, ClickAwayListener, useTheme} from "@mui/material";
import HeroSection from "../components/hero/HeroSection";
import homeHeroImageMonitor from "../assets/SM_heroBakgroundMonitor.jpg";
import homeHeroImageDesktop from "../assets/SM_heroBackgroundDesktop.jpg";
import homeHeroImageTablet from "../assets/SM_heroBackgroundTablet.jpg";
import homeHeroImageMobile from "../assets/SM_heroBackgroundMobile.jpg";
import {MarketingPhrasesRow} from "../components/marketing/MarketingPhrasesRow";
import leftArrow from "../assets/icons/SM_arrowLeft.png"
import rightArrow from "../assets/icons/SM_arrowRight.png"
import {CervinoHeaderText} from "../components/typography/CervinoHeaderText";
import ProductVerticalCard from "../components/layout/ProductVerticalCard/ProductVerticalCard";
import SMBestSellerClaim from "../components/marketing/SMBestSellerClaim";
import SMButtonBrown from "../components/navigation/SMButtonBrown";
import SMButtonWhite from "../components/navigation/SMButtonWhite";
import {SMOtherBrandsCard} from "../components/marketing/SMOtherBrandsCard";
import SMPhrasesScroller from "../components/marketing/SMPhrasesScroller";
import CardImageBackground from "../components/layout/CardImageBackground";
import MapChart from "../components/maps/MapChart";
import { STORES } from "../data/stores";
import { trackEvent } from "../utils/analytics";

// Images imports
import beanProduct from "../assets/products/beans/MEXICAN_ELOTE_STYLE_PINTO_AND_BLACK_BEANS/MEXICAN_ELOTE_STYLE_PINTO_AND_BLACK_BEANS.png"
import tamaleProduct from "../assets/products/tamales/STREET_CORN_TAMALE/STREET_CORN_TAMALE.png"
import riceCupProduct from "../assets/products/RICE_CUPS.png"
import mealProduct from "../assets/products/rices/pouches/POBLANO_RICE/POBLANO_RICE.png"
import decoration from "../assets/icons/SM_long_flower_white.png"
import smStar from "../assets/icons/SM_star.png"
import smAgricultureBackground from "../assets/backgrounds/SM_agricultureBackground.jpg"

// Claims
import seedOilFreeClaim from "../assets/claims/bestSellers/seedOilFree/seedOilFree.png"
import seedOilFreeClaimBg from "../assets/claims/bestSellers/seedOilFree/seedOilFreeBackground.jpg"

import boneBrothClaim from "../assets/claims/bestSellers/boneBroth/boneBroth.png"
import boneBrothClaimBg from "../assets/claims/bestSellers/boneBroth/boneBrothBackground.jpg"

import highProteinClaim from "../assets/claims/bestSellers/highProtein/highProtein.png"
import highProteinClaimBg from "../assets/claims/bestSellers/highProtein/highProteinBackground.jpg"

import avocadoOilClaim from "../assets/claims/bestSellers/avocadoOil/avocadoOil.png"
import avocadoOilClaimBg from "../assets/claims/bestSellers/avocadoOil/avocadoOilBackground.jpg"

import microwaveReadyClaim from "../assets/claims/bestSellers/microwaveReady/microwaveReady.png"
import microwaveReadyClaimBg from "../assets/claims/bestSellers/microwaveReady/microwaveReadyBackground.png"


import SMFooter from "../components/marketing/SMFooter";

const AMAZON_STORE_URL =
    "https://www.amazon.com/stores/SANMIGUELFAMILYFOODS/page/F5141099-76A8-4694-A0A3-660953697105?lp_asin=B0H1JMHBF9&ref_=cm_sw_r_ud_ast_store_J5GAX386Y313GMWZVFTB&store_ref=bl_ast_dp_brandlogo_sto";

const HERO_MOBILE_IMAGE_WIDTH = 1080;
const HERO_MOBILE_IMAGE_HEIGHT = 1920;
const HERO_LOGO_TOP_RATIO = 0.717;
const HERO_LOGO_BOTTOM_RATIO = 0.805;
const HERO_LOGO_BUTTON_GAP = 24;
const HERO_BUTTON_MAX_VIEWPORT_WIDTH = 900;
const HERO_TABLET_MIN_VIEWPORT_WIDTH = 600;

function getHeroButtonPlacement() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w <= 0 || h <= 0) return null;
    if (w >= HERO_BUTTON_MAX_VIEWPORT_WIDTH) return null;

    const scale = Math.max(w / HERO_MOBILE_IMAGE_WIDTH, h / HERO_MOBILE_IMAGE_HEIGHT);
    const drawnHeight = HERO_MOBILE_IMAGE_HEIGHT * scale;
    const offsetY = (h - drawnHeight) / 2;

    const logoTopY = offsetY + HERO_LOGO_TOP_RATIO * drawnHeight;
    const logoBottomY = offsetY + HERO_LOGO_BOTTOM_RATIO * drawnHeight;

    const isTabletWidth = w >= HERO_TABLET_MIN_VIEWPORT_WIDTH;
    return isTabletWidth
        ? { bottom: h - logoTopY + HERO_LOGO_BUTTON_GAP }
        : { top: logoBottomY + HERO_LOGO_BUTTON_GAP };
}

export default function HomePage() {
    const theme = useTheme();
    const findUsRef = React.useRef(null);

    const items = [
        { id: "beans", title: "Beans", buttonLabel: "BEANS", bgColor: "#81634B", imageSrc: beanProduct, to: "/beans" },
        { id: "tamales", title: "Tamales", buttonLabel: "TAMALES", bgColor: "#F8A716", imageSrc: tamaleProduct, to: "/tamales" },
        { id: "rice", title: "Rice Cups", buttonLabel: "RICE CUPS", bgColor: "#792B25", imageSrc: riceCupProduct, to: "/rices" },
        { id: "meals", title: "Meals", buttonLabel: "MEALS", bgColor: "#525E3B", imageSrc: mealProduct, to: "/rices" },
    ];

    const [openClaim, setOpenClaim] = React.useState(null);
    const [heroButtonPlacement, setHeroButtonPlacement] = React.useState(() => getHeroButtonPlacement());
    const lastHeroWidthRef = React.useRef(null);

    React.useEffect(() => {
        const recompute = () => {
            if (window.innerWidth <= 0 || window.innerHeight <= 0) return;
            setHeroButtonPlacement(getHeroButtonPlacement());
            lastHeroWidthRef.current = window.innerWidth;
        };
        // The hero background is pinned to 100svh (a fixed height) so it doesn't
        // rescale as mobile browsers show/hide their toolbar while scrolling —
        // but window.innerHeight itself still fluctuates when that happens. Only
        // recomputing when WIDTH changes (a real resize/rotation) keeps the button
        // anchored to that now-stationary background instead of drifting with
        // every toolbar wobble.
        const handleResize = () => {
            if (window.innerWidth === lastHeroWidthRef.current) return;
            recompute();
        };
        const raf = requestAnimationFrame(recompute);
        window.addEventListener("resize", handleResize);
        window.addEventListener("orientationchange", recompute);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("orientationchange", recompute);
        };
    }, []);

    const handleScrollToFindUs = () => {
        findUsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleHeroBuyNow = () => {
        trackEvent("select_content", {
            content_type: "buy_now_home_page",
            item_name: "Hero CTA",
            destination_url: AMAZON_STORE_URL,
        });
        window.open(AMAZON_STORE_URL, "_blank", "noopener,noreferrer");
    };

    return (
        <Stack spacing={0}>
            <HeroSection images={{
                monitor: homeHeroImageMonitor,
                desktop: homeHeroImageDesktop,
                tablet: homeHeroImageTablet,
                mobilePortrait: homeHeroImageMobile,
            }}>
                {heroButtonPlacement && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 2,
                            top: heroButtonPlacement.top != null ? `${heroButtonPlacement.top}px` : undefined,
                            bottom: heroButtonPlacement.bottom != null ? `${heroButtonPlacement.bottom}px` : undefined,
                        }}
                    >
                        <SMButtonWhite onClick={handleHeroBuyNow} sx={{ px: 4 }}>
                            BUY NOW
                        </SMButtonWhite>
                    </Box>
                )}

                <Box
                    sx={{
                        display: { xs: "none", md: "block" },
                        position: "absolute",
                        top: {md: 110, lg: 100, xl: 90 },
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2,
                    }}
                >
                    <SMButtonBrown onClick={handleHeroBuyNow} sx={{ px: 3 }}>
                        BUY NOW
                    </SMButtonBrown>
                </Box>
            </HeroSection>

            <Stack sx={{ py: { xs: 2, sm: 0 } }}>
                <MarketingPhrasesRow
                    leftSrc={rightArrow}
                    rightSrc={leftArrow}
                    color={theme.palette.primary.main}
                    phrases={["Real Food", "Real People", "Real Moments"]}
                />

                <Stack
                    sx={{
                        mt: {
                            xs: 0,
                            sm: -4,
                            md: -6,
                        },
                    }}
                >
                    <CervinoHeaderText color={theme.palette.primary.main} headerText="BEST SELLERS" />
                </Stack>

                <Box
                    sx={{
                        px: { xs: 2, sm: 4, md: 6 },
                        mt: 2,
                        display: "grid",
                        gap: 3,
                        gridTemplateColumns: {
                            xs: "1fr",                // 1 column
                            md: "repeat(2, 1fr)",      // 2 columns
                            lg: "repeat(4, 1fr)",      // 4 columns
                        },
                        alignItems: "center",
                    }}
                >
                    {items.map((item, index) => (
                        <ProductVerticalCard
                            key={item.id}
                            bgColor={item.bgColor}
                            imageSrc={item.imageSrc}
                            imageAlt={item.title}
                            buttonLabel={item.buttonLabel}
                            to={item.to}
                            decorationSrc={decoration}
                            decorationVariant={index % 2 === 0 ? "tl-br" : "tr-bl"}
                        />
                    ))}
                </Box>

                <ClickAwayListener onClickAway={() => setOpenClaim(null)}>
                    <Box
                        sx={{
                            px: { sm: 5, md: 10, lg: 15, xl: 20 },
                            mt: { xs: 2, sm: 2, md: 2, lg: 3 },
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(6, 1fr)",
                                sm: "repeat(5, minmax(0, 1fr))",
                            },
                            alignItems: "center",
                            justifyItems: "center",
                            width: "100%",
                        }}
                    >
                        <SMBestSellerClaim
                            frontImageSrc={seedOilFreeClaim}
                            backImageSrc={seedOilFreeClaimBg}
                            backText="Made without seed oils for cleaner, better nutrition."
                            size={{ xs: "clamp(88px, 24vw, 130px)", sm: "clamp(56px, 12vw, 250px)" }}
                            pinned={openClaim === "seedOil"}
                            onPinnedChange={(next) => setOpenClaim(next ? "seedOil" : null)}
                            sx={{ gridColumn: { xs: "1 / span 2", sm: "auto" } }}
                        />

                        <SMBestSellerClaim
                            frontImageSrc={boneBrothClaim}
                            backImageSrc={boneBrothClaimBg}
                            backText="Bone broth that adds real flavor and real benefits."
                            size={{ xs: "clamp(88px, 24vw, 130px)", sm: "clamp(56px, 12vw, 250px)" }}
                            pinned={openClaim === "boneBroth"}
                            onPinnedChange={(next) => setOpenClaim(next ? "boneBroth" : null)}
                            sx={{ gridColumn: { xs: "3 / span 2", sm: "auto" } }}
                        />

                        <SMBestSellerClaim
                            frontImageSrc={highProteinClaim}
                            backImageSrc={highProteinClaimBg}
                            backText="Protein that keeps you full, energized, and ready to go."
                            size={{ xs: "clamp(88px, 24vw, 130px)", sm: "clamp(56px, 12vw, 250px)" }}
                            pinned={openClaim === "highProtein"}
                            onPinnedChange={(next) => setOpenClaim(next ? "highProtein" : null)}
                            sx={{ gridColumn: { xs: "5 / span 2", sm: "auto" } }}
                        />

                        <SMBestSellerClaim
                            frontImageSrc={avocadoOilClaim}
                            backImageSrc={avocadoOilClaimBg}
                            backText="Cooked with avocado oil for clean flavor and clean fuel."
                            size={{ xs: "clamp(88px, 24vw, 130px)", sm: "clamp(56px, 12vw, 250px)" }}
                            pinned={openClaim === "avocadoOil"}
                            onPinnedChange={(next) => setOpenClaim(next ? "avocadoOil" : null)}
                            sx={{ gridColumn: { xs: "2 / span 2", sm: "auto" } }}
                        />

                        <SMBestSellerClaim
                            frontImageSrc={microwaveReadyClaim}
                            backImageSrc={microwaveReadyClaimBg}
                            backText="Hot and ready in seconds, perfect for busy days."
                            size={{ xs: "clamp(88px, 24vw, 130px)", sm: "clamp(56px, 12vw, 250px)" }}
                            pinned={openClaim === "microwaveReady"}
                            onPinnedChange={(next) => setOpenClaim(next ? "microwaveReady" : null)}
                            sx={{ gridColumn: { xs: "4 / span 2", sm: "auto" } }}
                        />
                    </Box>
                </ClickAwayListener>

                <Box sx={{ textAlign: 'center', paddingTop: { xs: 2, sm: 3 } }}>
                    <SMButtonBrown to="/products"  sx={{width: {xs: "80%", md: "40%", lg: "30%", xl: "20%"}}}>
                        ALL PRODUCTS
                    </SMButtonBrown>
                </Box>
            </Stack>

            <Box sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 4, md: 5, lg: 1} }}>
                <SMOtherBrandsCard
                    backgroundSx={{
                        backgroundColor: "#B77B2F",
                        color: "#ffffff",
                    }}
                    onFindUsClick={handleScrollToFindUs}
                    features={[
                        "AUTHENTIC MEXICAN RECIPES",
                        "HIGH PROTEIN",
                        "BONE BROTH",
                        "NO SEED OILS",
                        "BEEF TALLOW",
                        "PANTRY STABLE",
                    ]}
                    columns={[
                        {
                            key: "sanmiguel",
                            type: "positive",
                            header: (
                                <Box
                                    component="img"
                                    src="/images/SM_whiteLogo.png"
                                    alt="San Miguel"
                                    sx={{
                                        height: { xs: 50, sm: 50, md: 50, lg: 60, xl: 80 },
                                        width: "auto",
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                        display: "block",
                                    }}
                                />
                            ),
                        },
                        {
                            key: "other",
                            type: "negative",
                            header: "OTHER\n BRANDS",
                        },
                    ]}
                />
            </Box>

            <Box>
                <SMPhrasesScroller
                    phrases={[
                        "Food you love, made better.",
                        "Real Mexican flavor, ready in seconds.",
                        "Food made with love and ease, just like you would.",
                        "Good food for busy days. Better food for everyday.",
                        "Wholesome ingredients, bold flavor, zero effort.",
                        "Tastier, healthier, more convenient Mexican meals.",
                    ]}
                    iconSrc={smStar}
                    iconRepeat={3}
                    speedSeconds={30}
                    textColor={theme.palette.primary.main}
                    pauseOnHover={false}
                />
            </Box>

            <Box paddingTop={{xs: 2, sm: 3}} paddingX={{xs: 2, sm: 4, md: 5, lg: 1}}>
                <CardImageBackground
                    imageSrc={smAgricultureBackground}
                    height={{ xs: 180, sm: 240, md: 320, lg: 450, xl: 500 }}
                    overlayOpacity={0.45}
                    content={
                        <CervinoHeaderText
                            sx={{
                                fontSize: { xs: "1.8rem", sm: "3rem", md: "4rem", lg: "5rem", xl: "6rem" },
                                whiteSpace: "pre-line",
                            }}
                        >
                            {"Authentic Mexican flavors\n for your busy day."}
                        </CervinoHeaderText>
                    }
                />
            </Box>

            <Box ref={findUsRef} id="find-us">
                <Box paddingTop={{xs: 2, sm: 3}} paddingX={{xs: 2, sm: 4, md: 5, lg: 1}}>
                    <CervinoHeaderText
                        sx={{fontSize: { xs: "1.8rem", sm: "3rem", md: "4rem", lg: "5rem", xl: "6rem" }}}
                        color={theme.palette.primary.main}
                    >
                        FIND SAN MIGUEL NEAR YOU
                    </CervinoHeaderText>
                </Box>

                <MapChart stores={STORES} />

                <Box textAlign="center">
                    <SMButtonBrown to="/wheretobuy"  sx={{width: {xs: "80%", md: "40%", lg: "30%", xl: "20%"}}}>
                        WHERE TO BUY
                    </SMButtonBrown>
                </Box>
            </Box>

            <Box marginTop={{ xs: 2, sm: 3 }}>
                <SMFooter></SMFooter>
            </Box>
        </Stack>
    );
}